import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavbarService } from '../../core/navbar.service';
import { SeoService } from '../../core/seo.service';
import { ContentfulService } from '../../core/contentful.service';
import { TOUR_STOPS, TourStop } from './tour.data';

@Component({
  selector: 'app-tour',
  templateUrl: './tour.component.html',
  styleUrls: ['./tour.component.css']
})
export class TourComponent implements OnInit, OnDestroy {
  tourStops: TourStop[] = TOUR_STOPS;
  currentStopIndex: number = 0;
  isAudioPlaying: boolean = false;
  speechSynthesisSupported: boolean = false;
  private speechUtterance: SpeechSynthesisUtterance | null = null;

  get currentStop(): TourStop {
    return this.tourStops[this.currentStopIndex];
  }

  get progressPercentage(): number {
    return Math.round(((this.currentStopIndex + 1) / this.tourStops.length) * 100);
  }

  constructor(
    public nav: NavbarService,
    private seo: SeoService,
    private contentful: ContentfulService
  ) {}

  ngOnInit(): void {
    this.nav.show();
    this.seo.updateTags({
      title: 'Self-Guided Museum Walking Tour & Audio Guide',
      description: 'Explore the Bayou Lacombe Museum with our interactive self-guided tour. Discover 6 curated stops covering the 1912 wooden schoolhouse, Choctaw artifacts, Chahta-Ima, and native botanical gardens.',
      url: '/tour'
    });

    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.speechSynthesisSupported = true;
    }

    // Attempt to load dynamic tour stops from Contentful
    this.contentful.getTourStops().then(entries => {
      if (entries && entries.length > 0) {
        this.tourStops = entries.map((entry, idx) => {
          const f = entry.fields;
          return {
            id: f.order || idx + 1,
            slug: f.slug || `stop-${idx + 1}`,
            title: f.title || '',
            subtitle: f.subtitle || '',
            category: f.category || 'indoor',
            categoryLabel: f.categoryLabel || 'Exhibition',
            location: f.location || 'Museum Grounds',
            description: f.description || '',
            historicalContext: f.historicalContext || '',
            imageUrl: f.image?.fields?.file?.url ? `https:${f.image.fields.file.url}` : TOUR_STOPS[idx % TOUR_STOPS.length].imageUrl,
            lookFor: Array.isArray(f.lookFor) ? f.lookFor : (typeof f.lookFor === 'string' ? f.lookFor.split('\n') : [])
          };
        });
      }
    }).catch(() => {
      // Contentful model not yet published or empty, gracefully keep TOUR_STOPS
    });
  }

  goToStop(index: number): void {
    if (index >= 0 && index < this.tourStops.length) {
      this.stopNarration();
      this.currentStopIndex = index;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextStop(): void {
    if (this.currentStopIndex < this.tourStops.length - 1) {
      this.goToStop(this.currentStopIndex + 1);
    }
  }

  previousStop(): void {
    if (this.currentStopIndex > 0) {
      this.goToStop(this.currentStopIndex - 1);
    }
  }

  toggleAudioNarration(): void {
    if (!this.speechSynthesisSupported) return;

    if (this.isAudioPlaying) {
      this.stopNarration();
    } else {
      this.startNarration();
    }
  }

  private startNarration(): void {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    
    window.speechSynthesis.cancel();
    const narrationText = `${this.currentStop.title}. ${this.currentStop.subtitle}. Located at ${this.currentStop.location}. ${this.currentStop.description} ${this.currentStop.historicalContext}`;
    
    this.speechUtterance = new SpeechSynthesisUtterance(narrationText);
    this.speechUtterance.rate = 0.95;
    this.speechUtterance.onend = () => {
      this.isAudioPlaying = false;
    };
    this.speechUtterance.onerror = () => {
      this.isAudioPlaying = false;
    };

    window.speechSynthesis.speak(this.speechUtterance);
    this.isAudioPlaying = true;
  }

  private stopNarration(): void {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    this.isAudioPlaying = false;
  }

  ngOnDestroy(): void {
    this.stopNarration();
  }
}
